import { useController, useFormContext } from 'react-hook-form'

import { RadioGroup } from '@/components/ui/radio'
import { TextField } from '@/components/ui/text-field'
import {
  QUESTIONNAIRE_FORM_ID,
  communicationOptions,
  referralOptions,
} from '@/modules/questionnaire/consts'
import { isSpecifiableReferralOption } from '@/modules/questionnaire/utils'

import type { QuestionnaireFormValues } from '../../../types'
import { QuestionnaireFormButton } from '../../components/button'
import { Field } from '../../components/field'
import { RadioButton } from '../../components/radio-button'
import { QuestionnaireStep } from '../../components/step'
import { PreferredCommunicationSpecifier } from './preferred-communication-specifier'
import { ReferralSpecifier } from './referral-specifier'

interface Props {
  onClickBack: () => void
}

export const PersonalInfoStep = ({ onClickBack }: Props) => {
  const { control, formState } = useFormContext<QuestionnaireFormValues>()
  const preferredCommunication = useController({
    control,
    name: 'preferredCommunication',
  })
  const referral = useController({
    control,
    name: 'referral',
  })
  const email = useController({
    control,
    name: 'Email',
  })

  const actions = (
    <>
      <QuestionnaireFormButton
        variant="outline"
        className="w-[100px] bg-transparent"
        type="button"
        onClick={onClickBack}
      >
        Back
      </QuestionnaireFormButton>
      <QuestionnaireFormButton
        disabled={!formState.isValid}
        isLoading={formState.isSubmitting}
        className="w-[120px]"
        type="submit"
        form={QUESTIONNAIRE_FORM_ID}
      >
        Submit
      </QuestionnaireFormButton>
    </>
  )
  const communicationValue = preferredCommunication.field.value
  const referralValue = referral.field.value

  return (
    <QuestionnaireStep
      title="Tell us a bit about yourself"
      description="We'd love to know more about you to tailor your CreatorHero experience."
      actions={actions}
      className="space-y-6"
    >
      <Field
        label="Best way to talk"
        isRequired
      >
        <RadioGroup
          {...preferredCommunication.field}
          className="flex gap-2.5"
        >
          {communicationOptions.map(({ value, label }) => (
            <RadioButton
              key={value}
              value={value}
              variant="small"
            >
              {label}
            </RadioButton>
          ))}
        </RadioGroup>
      </Field>
      {!!communicationValue && (
        <PreferredCommunicationSpecifier
          type={communicationValue}
          key={communicationValue}
        />
      )}
      <Field label="E-Mail Address">
        <TextField
          placeholder="(optional)"
          {...email.field}
        />
      </Field>
      <Field
        label="How did you hear about CreatorHero?"
        isRequired
      >
        <RadioGroup
          {...referral.field}
          className="flex flex-wrap gap-2.5"
        >
          {referralOptions.map(({ value, label }) => (
            <RadioButton
              key={value}
              value={value}
              variant="small"
            >
              {label}
            </RadioButton>
          ))}
        </RadioGroup>
      </Field>
      {isSpecifiableReferralOption(referralValue) && (
        <ReferralSpecifier
          type={referralValue}
          key={referralValue}
        />
      )}
    </QuestionnaireStep>
  )
}
